
import React from 'react';
import Hero from './Hero.tsx';
import { ProblemSection, OpportunitySection } from './HomeContent.tsx';
import { Shield, Users, Activity, Brain, ArrowRight, Lock, Globe, CheckCircle } from 'lucide-react';
import { HOW_IT_WORKS } from '../constants.tsx';

const HomePage: React.FC = () => {
  return (
    <div className="space-y-0">
      <Hero />
      
      <div className="relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-32 bg-gradient-to-b from-brand-blue/50 to-transparent"></div>
        <ProblemSection />
      </div>

      <OpportunitySection />

      {/* Trust & Sovereignty Section */}
      <section className="py-40 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
           <div className="space-y-12">
              <h2 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black">Foundation of Trust</h2>
              <h3 className="text-5xl md:text-7xl font-serif italic tracking-tighter leading-none text-white">Security as Infrastructure.</h3>
              <p className="text-2xl text-white font-bold leading-relaxed">
                Designed for data sovereignty and absolute verifiability. Your data stays where you need it.
              </p>
              <div className="grid gap-6">
                {[
                  { icon: <Lock className="text-brand-blue"/>, text: "Data Sovereignty: Localized storage compliance" },
                  { icon: <CheckCircle className="text-brand-blue"/>, text: "Verifiability: Identity-based node verification" },
                  { icon: <Globe className="text-brand-blue"/>, text: "Global Reach: Working to add all language support" },
                  { icon: <Activity className="text-brand-blue"/>, text: "Whitelabeling: Customized to your organization and language" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-4 p-6 glass-island rounded-2xl border-white/10">
                    <div className="w-10 h-10 rounded-lg bg-brand-blue/10 flex items-center justify-center">{item.icon}</div>
                    <span className="text-xl font-bold text-white">{item.text}</span>
                  </div>
                ))}
              </div>
           </div>
           <div className="relative">
              <div className="glass-island p-16 rounded-[4rem] text-center space-y-12 relative z-10 border-white/10 bg-brand-blue/[0.02] animate-float">
                 <Shield size={80} className="text-brand-blue mx-auto" />
                 <h4 className="text-4xl font-serif italic text-white tracking-tighter leading-none">Your Platform. <br/> Your Rules.</h4>
                 <p className="text-white text-xl font-medium">Boulai provides the framework: you provide the sovereignty. Completely whitelabelable to fit your government's identity.</p>
              </div>
           </div>
        </div>
      </section>

      {/* Unified Ecosystem Section */}
      <section className="py-40 px-6 bg-white/[0.02] border-y border-white/5 relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
           <div className="space-y-12">
              <h2 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black">Integrated Growth</h2>
              <h3 className="text-5xl md:text-7xl font-serif italic tracking-tighter leading-none text-white">Scale with Boulai.</h3>
              <p className="text-2xl text-white font-bold leading-relaxed">
                PolicyNow is the starting engagement section that grows as soon as you are ready to expand your viewership base.
              </p>
              <div className="grid gap-6">
                {[
                  "PolicyNow: Included for you as a leader",
                  "Smart City: Functions roll out as they become available",
                  "ERS Solutions: Native integration into your stack",
                  "Integrations: Grow as we create them for your constituency"
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-4 p-6 glass-island rounded-2xl border-white/10">
                    <div className="w-8 h-8 rounded-lg bg-brand-blue/10 flex items-center justify-center text-brand-blue"><Activity size={18}/></div>
                    <span className="text-lg font-bold text-white">{item}</span>
                  </div>
                ))}
              </div>
           </div>
           <div className="relative">
              <div className="glass-island p-16 rounded-[4rem] text-center space-y-12 relative z-10 border-white/10 shadow-2xl">
                 <div className="w-24 h-24 bg-brand-blue rounded-full flex items-center justify-center text-white mx-auto shadow-2xl shadow-brand-blue/30">
                    <Brain size={48} />
                 </div>
                 <h4 className="text-3xl font-serif italic text-white tracking-tighter leading-tight">Benefit your citizens, <br/> people, or constituency.</h4>
                 <p className="text-white text-lg font-medium opacity-60">Grow with Boulai and access the full suite of governance intelligence as it evolves.</p>
                 <a href="#trust" className="inline-flex items-center space-x-3 text-brand-blue font-black uppercase text-xs tracking-widest hover:underline">
                   <span>Learn about Trust</span>
                   <ArrowRight size={14} />
                 </a>
              </div>
           </div>
        </div>
      </section>
      
      <section className="py-40 px-6 max-w-7xl mx-auto relative overflow-hidden">
        <div className="text-center mb-32">
          <h2 className="text-[10px] uppercase tracking-[0.5em] text-brand-blue font-black mb-10">THE STACK</h2>
          <h3 className="text-5xl md:text-8xl font-black mb-12 uppercase tracking-tighter leading-none text-white">Intelligence Hub<span className="text-brand-blue">.</span></h3>
          <p className="text-white text-3xl max-w-3xl mx-auto leading-relaxed font-black">
            Aggregating data from verified inputs, analyzing with neural models, and outputting clear governance intelligence.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {HOW_IT_WORKS.map((step, i) => (
            <div key={i} className="group p-12 glass-island rounded-[2rem] hover:border-brand-blue/30 transition-all duration-700 bg-white/[0.01]">
              <div className="w-16 h-16 bg-brand-blue/10 rounded-2xl flex items-center justify-center mb-10 text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-all shadow-xl shadow-brand-blue/5">
                {i === 0 && <Users size={30} />}
                {i === 1 && <Shield size={30} />}
                {i === 2 && <Brain size={30} />}
                {i === 3 && <Activity size={30} />}
              </div>
              <h4 className="text-2xl font-black mb-6 uppercase tracking-tighter leading-none text-white">{step.title}</h4>
              <p className="text-lg text-white font-bold leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;